// Modules
import { useState, useMemo } from "react";
import pick from "lodash.pick";
import { addYears, isDate, startOfYear, startOfDay, endOfDay } from "date-fns";
// Helpers
import {
    EVENT_CANCELED,
    EVENT_FREE_SLOT,
    EVENT_SESSION,
    THERAPIST_ABSENCE_EVENT_TYPES
} from "../../../helpers/constants";
import { getEventDates } from "../../UITemplate/views/calendar/helpers/rrule";
import { addBookedDates, addExceptionDates } from "../../UITemplate/views/calendar/helpers/calendar";
import { IS_ACTIVE } from "../../../helpers/constants_db";
import type { TherapistWithEvents } from "@/lib/types/therapist/calendar";
import type { Color } from "@/helpers/colors";
import type { UserEventWithTherapist } from "@/lib/types/types";

const DEFAULT_YEARS = 2;

type UseRenderAdminEventsProps = {
    users: TherapistWithEvents[] | undefined;
    calendars: Record<string, boolean>;
    getColorByIndex: (index: number) => Color;
};

type UseRenderAdminEventsReturnType = {
    events: UserEventWithTherapist[];
    handleDatesSet: (date: Date) => void;
};

const useRenderAdminEvents = ({
    users,
    calendars,
    getColorByIndex
}: UseRenderAdminEventsProps): UseRenderAdminEventsReturnType => {
    const [yearsDifference, setYearsDifference] = useState(1);

    const handleDatesSet = (date: Date) => {
        const currentDate = new Date();
        const firstDayOfYear = startOfYear(currentDate);
        const newYearDifference = Math.abs(date.getFullYear() - firstDayOfYear.getFullYear()) + DEFAULT_YEARS;

        if (yearsDifference !== newYearDifference && newYearDifference !== 0) {
            setYearsDifference(newYearDifference);
        }
    };

    const transformEvents = (uc: TherapistWithEvents, color: Color) => event => {
        let eventColor: string | null = null;
        let bookedDates: string[] = [];

        if ([...THERAPIST_ABSENCE_EVENT_TYPES, EVENT_SESSION].includes(event.type)) {
            eventColor = color.session;
        }

        if (event.type === EVENT_CANCELED) {
            eventColor = color.canceled;
        }

        if (event.type === EVENT_FREE_SLOT) {
            eventColor = color.free_slot;

            const matchingSessions = uc.events.filter(
                session => session.type === EVENT_SESSION && event?.session_ids?.includes(session.id)
            );

            matchingSessions.forEach(session => {
                const { rrule } = session || {};
                const { dtstart, until } = rrule || {};

                const currentDate = new Date();
                const oneYearLater = addYears(currentDate, yearsDifference);

                const startFrom = dtstart && startOfDay(new Date(dtstart));
                const untilDate = until && endOfDay(new Date(until));
                const startTo = rrule ? (isDate(untilDate) ? untilDate : endOfDay(oneYearLater)) : undefined;

                const dates = getEventDates({ event: session, startFrom, startTo });
                bookedDates.push(...dates);
            });
        }

        if (bookedDates.length > 0) {
            addBookedDates(event, bookedDates);
            addExceptionDates(event, bookedDates);
        }

        return {
            ...event,
            color: eventColor,
            user: pick(uc, ["_id", "name", "email"])
        };
    };

    const memoizedEvents = useMemo(() => {
        if (!users || users.length === 0) {
            return [];
        }

        return users
            .map((uc, index) => {
                const color = getColorByIndex(index);
                return uc.events.map(transformEvents(uc, color));
            })
            .flat()
            .filter(event => calendars[event.user._id] && event.type !== EVENT_CANCELED && event[IS_ACTIVE] !== false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [users, calendars, getColorByIndex]);

    return { events: memoizedEvents, handleDatesSet };
};

export default useRenderAdminEvents;
